export type AdminUIMode = "legacy" | "tenant" | "catalog";

type RuntimeConfig = Readonly<{
  mode: AdminUIMode;
  apiBaseUrl: string;
  keycloakUrl: string;
  keycloakRealm: string;
  keycloakClientId: string;
}>;

const runtimeConfig = readRuntimeConfig();
const runtimeConfigRequired = import.meta.env.VITE_RUNTIME_CONFIG_REQUIRED === "1";
if (runtimeConfigRequired && runtimeConfig === undefined) {
  throw new Error("Licensing runtime configuration is required");
}
const configuredMode = (runtimeConfig?.mode || import.meta.env.VITE_ADMIN_UI_MODE || "legacy").trim().toLowerCase();
if (configuredMode !== "legacy" && configuredMode !== "tenant" && configuredMode !== "catalog") {
  throw new Error("VITE_ADMIN_UI_MODE must be legacy, tenant or catalog");
}

const uiMode = configuredMode as AdminUIMode;

export const env = {
  apiBaseUrl: runtimeConfig?.apiBaseUrl || import.meta.env.VITE_ADMIN_API_BASE_URL || "",
  adminApiPrefix: uiMode === "catalog" ? "/catalog/v1" : "/admin/v1",
  uiMode,
  keycloakUrl: runtimeConfig?.keycloakUrl || import.meta.env.VITE_KEYCLOAK_URL || "",
  keycloakRealm: runtimeConfig?.keycloakRealm || import.meta.env.VITE_KEYCLOAK_REALM || (runtimeConfigRequired ? "" : "dserver.by"),
  keycloakClientId: runtimeConfig?.keycloakClientId || import.meta.env.VITE_KEYCLOAK_CLIENT_ID || (runtimeConfigRequired ? "" : "licensing-admin-ui"),
  e2eAuthToken: import.meta.env.VITE_E2E_AUTH_TOKEN || "",
  e2eAuthUser: import.meta.env.VITE_E2E_AUTH_USER || "admin-ui-e2e",
  e2eAuthRoles: import.meta.env.VITE_E2E_AUTH_ROLES || "licensing-admin"
};

function readRuntimeConfig(): RuntimeConfig | undefined {
  const value = window.__LICENSING_ADMIN_CONFIG__;
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Licensing runtime configuration is invalid");
  }
  const allowed = new Set(["mode", "apiBaseUrl", "keycloakUrl", "keycloakRealm", "keycloakClientId"]);
  if (Object.keys(value).some((key) => !allowed.has(key))) {
    throw new Error("Licensing runtime configuration contains an unsupported field");
  }
  const mode = requiredString(value.mode, "mode").toLowerCase();
  if (mode !== "tenant" && mode !== "catalog") {
    throw new Error("Licensing runtime mode must be tenant or catalog");
  }
  return {
    mode,
    apiBaseUrl: exactHTTPOrigin(value.apiBaseUrl, "apiBaseUrl"),
    keycloakUrl: exactHTTPOrigin(value.keycloakUrl, "keycloakUrl"),
    keycloakRealm: boundedIdentifier(value.keycloakRealm, "keycloakRealm"),
    keycloakClientId: boundedIdentifier(value.keycloakClientId, "keycloakClientId")
  };
}

function requiredString(value: unknown, name: string) {
  if (typeof value !== "string" || value.trim() !== value || value.length === 0 || value.length > 256) {
    throw new Error(`Licensing runtime ${name} is invalid`);
  }
  return value;
}

function exactHTTPOrigin(value: unknown, name: string) {
  const raw = requiredString(value, name);
  const parsed = new URL(raw);
  if ((parsed.protocol !== "https:" && parsed.protocol !== "http:") || parsed.username || parsed.password || parsed.search || parsed.hash || parsed.pathname !== "/") {
    throw new Error(`Licensing runtime ${name} must be an exact HTTP origin`);
  }
  return parsed.origin;
}

function boundedIdentifier(value: unknown, name: string) {
  const raw = requiredString(value, name);
  if (!/^[A-Za-z0-9._-]{1,128}$/.test(raw)) {
    throw new Error(`Licensing runtime ${name} is invalid`);
  }
  return raw;
}
